import { useForm } from 'react-hook-form';
import { useAuthorization } from '@/hooks/use-authorization';
import {
  createDoctorProfileFormResolver,
  DoctorProfileFormValues,
  getDefaultDoctorProfileValues,
} from './schema';
import { DoctorProfileFormMode, isFieldVisible } from './types';

export type DoctorProfileUseForm = ReturnType<
  typeof useForm<DoctorProfileFormValues>
>;

export interface UseDoctorProfileFormOptions {
  mode: DoctorProfileFormMode;
  defaultValues?: Partial<DoctorProfileFormValues>;
}

/**
 * Hook for the unified doctor profile form
 * Creates a form with mode-appropriate validation and defaults
 */
export const useDoctorProfileForm = ({
  mode,
  defaultValues,
}: UseDoctorProfileFormOptions) => {
  const { isAdmin, isSupport, isCsm } = useAuthorization();
  const baseDefaults = getDefaultDoctorProfileValues(mode);

  const doctorProfileForm = useForm<DoctorProfileFormValues>({
    resolver: createDoctorProfileFormResolver(mode, {
      skipValidation: isAdmin() || isSupport() || isCsm(),
    }),
    defaultValues: {
      ...baseDefaults,
      ...defaultValues,
      // Ensure nested objects are properly merged
      picture: defaultValues?.picture ?? null,
      file: defaultValues?.file ?? null,
      // Only set address if visible for this mode
      address: isFieldVisible(mode, 'address')
        ? {
            city: '',
            line1: '',
            line2: '',
            postalCode: '',
            ...defaultValues?.address,
          }
        : null,
    },
  });

  return { doctorProfileForm };
};
